import { apply, put, delay, select } from 'redux-saga/effects';
import { errorNotification } from '../reducers/notification.reducers';
import {
  loadOrdersSucceeded,
  loadOrdersFailed,
  loadOrderSucceeded,
  loadOrderFailed,
  removeObsoleteOrders,
  selectOrder,
  proceedOrder as proceedOrderAction,
  declineOrder as declineOrderAction,
  loadOrder as loadOrderAction,
  setOrderSearchValue,
  addOrderToHistory,
  startPrintOrder,
  mapOrder,
  addNewOrderToQueue,
  setNewOrderAlertVisible,
  resetOrderSearchValue,
} from '../reducers/order.reducers';

import {
  setAlertSoundState,
} from '../reducers/app.reducers';

import isRKSVCode from '../utils/is-rksv-code';

const cleanLeadingZeros = (text) => {
  if (!text) {
    return text;
  }

  return text.replace(/\b0+/g, '');
};

// eslint-disable-next-line import/prefer-default-export
export function* loadOrders(orderApi) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.loadOrders,
    );

    if (response.errorCode) {
      yield put(loadOrdersFailed({ message: response.message || 'Failed to login' }));
      yield put(errorNotification(response.message || 'Failed to login'));
    } else {
      yield put(loadOrdersSucceeded({ orders: response }));
    }
  } catch (e) {
    yield put(loadOrdersFailed({ message: 'Failed to login' }));
  }
}

export function* loadOrder(orderApi, { payload }) {
  const { orderId, isNew } = payload;
  try {
    const response = yield apply(
      orderApi,
      orderApi.loadOrder,
      [orderId],
    );

    if (response.errorCode) {
      yield put(loadOrderFailed(orderId));
      yield put(errorNotification(response.message || 'Failed to load order'));
    } else {
      if (response.spotId && response.isSpotAssignedToWaiter == false) {
        yield delay(350);
        yield put(removeObsoleteOrders());
        return;
      }

      if (response.show && isNew) {
        yield put(setAlertSoundState(true));
      }

      const order = mapOrder(response);
      const localSettings = yield select(state => state.app.localSettings);

      if (response.show && localSettings.useEposPrinter && localSettings.autoprintNewOrders && !(yield select(state => state.orders.orders)).flatMap(order => order.id).includes(order.id)) {
        yield put(startPrintOrder(order));

        if (localSettings.printOrderTwice) {
          yield put(startPrintOrder(order));
        }
      }

      if (localSettings.openAlertForNewOrder && !(yield select(state => state.orders.orders)).flatMap(order => order.id).includes(order.id)) {
        if (isNew) {
          yield put(setNewOrderAlertVisible(true));
        }

        yield put(addNewOrderToQueue(order));
      }

      yield put(loadOrderSucceeded({ order: response }));
      yield delay(350);
      yield put(removeObsoleteOrders());
    }
  } catch (e) {
    yield put(loadOrdersFailed(orderId));
  }
}

export function* proceedOrder(orderApi, { payload }) {
  try {
    yield delay(200);

    const response = yield apply(
      orderApi,
      orderApi.proceedOrder,
      [payload],
    );

    if (response && response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to proceed order'));
    } else {
      const signalRconnected = yield select(state => state.app.signalRconnected);

      if (!signalRconnected) {
        yield put(loadOrderAction({ orderId: payload }));
      }
    }
  } catch (e) {
    yield put(errorNotification('Failed to proceed order'));
  }
}

export function* declineOrder(orderApi, { payload }) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.declineOrder,
      [payload],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to decline order'));
    }
  } catch (e) {
    yield put(errorNotification('Failed to decline order'));
  }
}

export function* scheduleSearchFieldReset(orderApi) {
  try {
    const localSettings = yield select(state => state.app.localSettings);

    if (localSettings && localSettings.resetSearch) {
      console.log('Reseting search in 60 seconds');
      yield delay(60000);
      yield put(setOrderSearchValue(''));
    }
  } catch (error) {
    console.error('Failed to reset search', error);
  }
}

export function* handleScan(orderApi, { payload }) {
  try {
    if (!payload) {
      return;
    }

    let cleanedPayload = payload.trim();

    let order = null;
    let didMatchUser = false;
    let didMatchRKSV = false;
    const orders = yield select(state => state.orders.orders);

    if (isRKSVCode(cleanedPayload)) {
      const response = yield apply(orderApi, orderApi.findOrderByInvoice, [{ code: cleanedPayload, locationId: 0 }]);

      if (response) {
        if (response.errorCode || !response.guid) {
          yield put(errorNotification('Bestellung nicht gefunden!'));
        } else {
          order = orders.find(o => o.guid == response.guid || o.parentOrderId == response.orderID);
          if (order) {
            didMatchRKSV = true;
          }
        }
      }
    } else {
      // try to get last guid in case we got code to long
      if (cleanedPayload.length > 36) {
        cleanedPayload = cleanedPayload.slice(cleanedPayload.length - 36);
      }

      cleanedPayload = cleanedPayload.replace(/[^0-9]+/g, '');

      const matchingOrders = orders.filter((o) => {
        const matchesGuid = o.guid && o.guid.replace(/[^0-9]+/g, '') === cleanedPayload;
        const matchesUser = o.userPublicIdentifier && cleanLeadingZeros(o.userPublicIdentifier.replace(/[^0-9]+/g, '')) === cleanLeadingZeros(cleanedPayload);

        if (!didMatchUser && matchesUser) {
          didMatchUser = matchesUser;
        }

        return matchesGuid || matchesUser;
      });

      console.log('Found matching orders: ', matchingOrders.length, ' did match by user identifier', didMatchUser);

      if (!didMatchUser && matchingOrders.length === 1) {
        order = matchingOrders[0];
      } else if (matchingOrders.length > 1 || (didMatchUser && matchingOrders.length > 0)) {
        yield put(setOrderSearchValue(cleanedPayload));
        yield put(selectOrder(null));

        yield put(resetOrderSearchValue());
        return;
      }
    }

    if (order == null) {
      yield put(errorNotification('Bestellung nicht gefunden!'));
      return;
    }

    if (!didMatchUser) {
      yield put(selectOrder(order));

      const localSettings = yield select(state => state.app.localSettings);

      // check for not false for migration reasons
      if (!localSettings || localSettings.autoNextOnScan !== false) {
        yield put(proceedOrderAction(order.id));
        yield delay(4000);
        const selectedOrder = yield select(state => state.orders.selectedOrder);

        if (selectedOrder && selectedOrder.id === order.id) {
          yield put(selectOrder(null));
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleOrderCardAction(orderApi, { payload }) {
  const { order, proceeded, description } = payload;

  let note; let
    blockUser;

  if (!proceeded) {
    const data = { note, blockUser } = yield select(state => state.alert);
    note = data.note;
    blockUser = data.blockUser;
  }

  yield put(addOrderToHistory({ order, description, note, blockUser, proceeded }));

  if (proceeded) {
    yield put(proceedOrderAction(order.id));
  } else {
    yield put(declineOrderAction({ order, note, blockUser }));
  }
}

export function* setMode() {
  (yield select(state => state.app.scrollTopOrderGridFunction))();
}

export function* setDeliveryTime(orderApi, { payload }) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.setDeliveryTime,
      [payload],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Lieferzeit konnte nicht gesetzt werden!'));
    }
  } catch (e) {
    yield put(errorNotification('Lieferzeit konnte nicht gesetzt werden!'));
  }
}
